/**
 * What an evidence reference is, stated once for both sides of the wire.
 *
 * ## What this replaces
 *
 * `ref()` in `shipments.ts` is `z.string().trim().min(1).max(200)`, and every guard named
 * for evidence reduces to `nonEmpty(evidenceRef)`. **The single character `"x"` satisfied
 * every evidence gate in this platform.** `06-open-questions.md` Q19 names the stake:
 * *"I4's 'adjudicated from this log' is empty exactly where it matters if these are
 * optional"* — and the parties cannot talk to each other, so adjudication from the log is
 * the entire dispute story by design.
 *
 * ## Why the reference is a content hash
 *
 * The obvious design — the server mints a reference, the client cites it — cannot work.
 * I5 requires a Courier at a door with no connectivity to capture evidence and have the
 * event be durable immediately; §8 makes hours of silence ordinary. There is no server to
 * ask.
 *
 * A **pre-minted** batch handed out at working-set refresh fails for a sharper reason than
 * "no server at the door": a pre-minted token is a *capability, not a commitment*. It says
 * nothing about the bytes, so it closes none of the gap this exists to close — and it adds
 * a failure D7 forbids outright, a Courier who exhausts the allocation mid-shift.
 *
 * So the reference **is** the SHA-256 of the artifact bytes, computed where the artifact was
 * captured. No round-trip, works in an outage, and tamper-evident in the way that matters.
 *
 * ## What tamper-evidence here does and does not buy — read this before trusting it
 *
 * The hash is computed **by the device**, and in a dispute the device is the thing under
 * suspicion. Precisely:
 *
 * - **Post-commit substitution is detectable by anyone, for ever, including against us.**
 *   Once the event is in the append-only store, bytes arriving later either hash to the
 *   committed value or are refused. Neither Ops nor an adjudicator nor the platform can
 *   swap the photo afterwards. That is the property a dispute six months later turns on.
 * - **It does not prove the photo is of the right door at the right time.** A rooted handset
 *   can commit the hash of anything. Geo stamps and ADR-0013's dual timestamps are what
 *   speak to that, and they are weaker claims than a hash *looks* like.
 *
 * `evidence.tsx` has always been honest that "captured" and "retrievable by an adjudicator"
 * are different claims. Becoming more cryptographic must not make this record less honest.
 *
 * ## Why this lives in `@tard/contracts` rather than the kernel
 *
 * The kernel's `hashProof` imports `node:crypto` and cannot be reached from the handset,
 * which computes the same digest through `expo-crypto`. Two implementations of one rule are
 * free to disagree — the failure `redactForLog` and `DOOR_REFUSALS` were both restructured
 * to prevent. So the *shape* is stated once, here, in the pure package the device already
 * depends on, and a test asserts both paths agree on a fixed byte vector.
 */
import { z } from 'zod';
/**
 * A photographed artifact: scheme version, then the lowercase-hex SHA-256 of its bytes.
 *
 * Three reasons the version prefix exists rather than bare hex, in ascending importance:
 *
 * 1. It matches the repository's shape (`ord_`, `bcd_`, `mch_`).
 * 2. It is **versioned**, and the answer to "how do we change this in two years" must not
 *    be "we cannot" on an append-only store.
 * 3. **It closes ADR-0044's near-miss by construction.** That slice recorded that a `bcd_`
 *    reference escapes `RAW_PHONE_PATTERNS` *only* because every character before a digit
 *    is a word character, and pinned it with a 20,000-sample run rather than an argument.
 *    A bare 64-hex token re-opens that question — `/\b0\d{1,2}[\s-]?\d{6,7}\b/` needs a
 *    word boundary before the `0`. Analysis says a 64-character hex run cannot match; the
 *    prefix means we do not have to rely on analysis.
 */
export const EVIDENCE_REF_PHOTO = /^evd1_[0-9a-f]{64}$/u;
/**
 * An attestation that no artifact exists — a declined camera, or one that would not open.
 *
 * §8 treats broken hardware as ordinary and D7 forbids anything that can strand a Courier
 * mid-Leg, so this path must survive. It is deliberately **not** hash-shaped: there is no
 * content to address, and minting something that looked like a digest would be a lie inside
 * the record I4 exists to make true. An adjudicator must be able to tell "there will never
 * be an image" from "the image has not synced yet" without inspecting anything else.
 */
export const EVIDENCE_REF_ATTESTED = /^evdm_[0-9A-HJKMNP-TV-Z]{26}$/u;
export function isPhotographedEvidenceRef(value) {
    return EVIDENCE_REF_PHOTO.test(value);
}
export function isAttestedEvidenceRef(value) {
    return EVIDENCE_REF_ATTESTED.test(value);
}
export function isEvidenceRef(value) {
    return isPhotographedEvidenceRef(value) || isAttestedEvidenceRef(value);
}
/**
 * The digest a photographed reference commits to — the inverse of how a handset mints one.
 *
 * `packages/courier-sync/src/evidence-ref.ts` builds `evd1_` + the lowercase hex SHA-256 of
 * the bytes. This reads it back, and lives beside that regex rather than as a `slice(5)` at a
 * call site because a prefix length repeated in two places is a prefix length that drifts the
 * day a `evd2_` exists.
 *
 * Undefined for an attestation, which addresses no content at all — the caller must decide
 * what to do about that rather than compare against an empty string.
 */
export function evidenceRefDigest(value) {
    return isPhotographedEvidenceRef(value) ? value.slice('evd1_'.length) : undefined;
}
/**
 * The wire shape for a field 02a demands a proof artifact for.
 *
 * Applied **only** to those fields. `contactRef` is a masked-relay session id, `sellerScanRef`
 * is a barcode scan, `rulingRef` is an Ops document, `availabilityRef` is a phone call, and
 * `custody.ts`'s `evidenceRef` is a **cash recount** — none of them addresses bytes, and
 * forcing a digest on them would be inventing a policy nobody ruled (§10.6). The trap is
 * real: "make `evidenceRef` a hash" reads like one change and is five.
 */
export const evidenceRefSchema = z
    .string()
    .trim()
    .refine(isEvidenceRef, {
    message: 'must be evd1_<64 lowercase hex> (the sha-256 of the artifact) or evdm_<26> (no artifact captured)',
});
/**
 * Evidence whose required shape depends on what item 28 has not yet ruled.
 *
 * A loss is evidenced by a police report, a checkpoint reference, or a Hub recount — none
 * of which is a photograph. So this accepts either an artifact reference or the free string
 * the field has always taken, and **grades neither**. When item 28 lands, the grading goes
 * here; until then, pretending a police report has a content hash would be worse than the
 * looseness it replaced.
 */
export const lossEvidenceRefSchema = z.string().trim().min(1).max(200);
/** Why an attestation exists — so an unusable camera is distinguishable from an avoided one. */
export const ATTESTED_REASONS = ['permission_denied', 'camera_failed'];
export const attestedReasonSchema = z.enum(ATTESTED_REASONS);
/**
 * The payload fields that carry an artifact reference — stated once, here.
 *
 * Both the zod schemas above and the stream fold that feeds the replay defence need to agree
 * on this list, and they are in different packages. A second copy would drift, and what
 * drifts is whether a photograph can be reused across three attempts.
 *
 * `sellerScanRef`, `contactRef`, `rulingRef` and `availabilityRef` are deliberately absent:
 * they reference a barcode scan, a relay session, an Ops document and a phone call. None
 * addresses bytes.
 */
const ARTIFACT_FIELDS = ['evidenceRef', 'conditionPhotosRef', 'intakeRef'];
/**
 * Every artifact reference in one event payload.
 *
 * Deliberately tolerant of shape — it reads events off an append-only stream written by
 * several milestones of code, so an unexpected payload must yield nothing rather than throw.
 * A fold that crashed on an old event would take out the guard that depends on it.
 */
export function evidenceRefsIn(event) {
    const payload = event.payload;
    if (typeof payload !== 'object' || payload === null)
        return [];
    const record = payload;
    const found = [];
    for (const field of ARTIFACT_FIELDS) {
        const value = record[field];
        if (typeof value === 'string' && isEvidenceRef(value))
            found.push(value);
    }
    // Proof of delivery is nested, and only the photographed kind addresses bytes.
    const pod = record['pod'];
    if (typeof pod === 'object' && pod !== null) {
        const { kind, ref } = pod;
        if (kind === 'photo' && typeof ref === 'string' && isEvidenceRef(ref))
            found.push(ref);
    }
    return found;
}
//# sourceMappingURL=evidence.js.map